import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { toast } from 'react-hot-toast';

/**
 * Composant d'affichage de l'historique des sources d'approvisionnement
 */
const SupplySourceHistory = ({ requestId }) => {
  const [loading, setLoading] = useState(true);
  const [supplySource, setSupplySource] = useState(null);
  const [error, setError] = useState(null);

  // Charger la source d'approvisionnement au chargement du composant
  useEffect(() => {
    if (requestId) {
      fetchSupplySource();
    }
  }, [requestId]);

  // Récupérer la source d'approvisionnement depuis l'API
  const fetchSupplySource = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.marketingValidation.getRequestSupplySource(requestId);
      setSupplySource(response.data.data);
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching supply source:', error);
      setError('Erreur lors du chargement de la source d\'approvisionnement');
      setLoading(false);
      
      // Ne pas afficher de toast pour les erreurs 404 (pas de source d'approvisionnement)
      if (error.response && error.response.status !== 404) {
        toast.error('Erreur lors du chargement de la source d\'approvisionnement');
      }
    }
  };

  // Si la demande n'a pas encore été validée par le marketing ou si la réponse indique qu'il n'y a pas de source
  if (!loading && (!supplySource || supplySource === null) && !error) {
    return (
      <div className="text-sm text-gray-500 italic">
        Aucune source d'approvisionnement n'a encore été attribuée à cette demande.
      </div>
    );
  }

  // Si une erreur s'est produite
  if (error) {
    return (
      <div className="text-sm text-red-500">
        {error}
      </div>
    );
  }

  // Pendant le chargement
  if (loading) {
    return (
      <div className="animate-pulse space-y-2">
        <div className="h-4 bg-gray-200 rounded w-3/4"></div>
        <div className="h-4 bg-gray-200 rounded w-1/2"></div>
      </div>
    );
  }

  // Afficher les détails de la source d'approvisionnement
  return (
    <div className="supply-source-history">
      <div className="mb-2">
        <span className="text-sm font-medium text-gray-500">Type de source :</span>
        <span className="ml-2 text-sm text-gray-900">
          {supplySource.category === 'internal' ? 'Site interne' : 'Distributeur'}
        </span>
      </div>
      
      <div className="mb-2">
        <span className="text-sm font-medium text-gray-500">Nom :</span>
        <span className="ml-2 text-sm text-gray-900">
          {supplySource.category === 'internal' 
            ? supplySource.internalSite?.name 
            : supplySource.distributor?.name}
        </span>
      </div>
      
      {((supplySource.category === 'internal' && supplySource.internalSite?.code) || 
        (supplySource.category === 'distributor' && supplySource.distributor?.code)) && (
        <div className="mb-2">
          <span className="text-sm font-medium text-gray-500">Code :</span>
          <span className="ml-2 text-sm text-gray-900">
            {supplySource.category === 'internal' 
              ? supplySource.internalSite?.code 
              : supplySource.distributor?.code}
          </span>
        </div>
      )}
      
      {supplySource.createdAt && (
        <div className="mb-2">
          <span className="text-sm font-medium text-gray-500">Date d'attribution :</span>
          <span className="ml-2 text-sm text-gray-900">
            {new Date(supplySource.createdAt).toLocaleDateString()}
          </span>
        </div>
      )}
      
      {supplySource.attributionNumber && (
        <div className="mb-2">
          <span className="text-sm font-medium text-gray-500">N° d'attribution :</span>
          <span className="ml-2 text-sm text-gray-900">
            {supplySource.attributionNumber}
          </span>
        </div>
      )}
      
      {supplySource.validator && (
        <div className="mb-2">
          <span className="text-sm font-medium text-gray-500">Validé par :</span>
          <span className="ml-2 text-sm text-gray-900">
            {supplySource.validator.name}
          </span>
        </div>
      )}
    </div>
  );
};

export default SupplySourceHistory;
